/**
 * BootGate Component
 *
 * Calls Central's switch-context endpoint at boot (after OIDC auth succeeds)
 * to validate workspace status and load entitlements.
 *
 * Auth method: Bearer token from Redux (NOT cookie-include).
 * Endpoint: GET ${VITE_CENTRAL_URL}/api/suite/switch-context
 *
 * Outcomes:
 * - 200: workspace open → store entitlements, render children
 * - 401: token rejected → clear auth, redirect to Central
 * - 409 + code WORKSPACE_CLOSED → show WorkspaceClosedOverlay
 * - Network error / 5xx / timeout → show CentralUnavailableOverlay (fail-closed)
 *
 * IMPORTANT:
 * - No auto-switch, no auto-retry loops.
 * - Timeout (30s) transitions to CentralUnavailableOverlay once (user retries manually).
 * - Workspace switching is manual only (isSwitchingWorkspace blocks boot effect).
 */

import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import { getSimulatedCentralRole } from '../../auth/simulatedCentralRole';
import { getSimulatedAppProfile } from '../../auth/simulatedAppProfile';
import { SCOPE_BUNDLES } from '../../../../shared/src/appRoles';
import {
  selectCentralAccessToken,
  selectIsCentralAuthenticated,
  selectBootCompleted,
  selectWorkspaceClosed,
  selectCentralUnavailable,
  selectIsSwitchingWorkspace,
  setBootReady,
  setBootUnavailable,
  updateCentralAccessToken,
  centralLogout,
} from '../../store/slices/centralAuthSlice';
import { setBootUnauthenticated } from '../../store/slices/bootContextSlice';
import { classifyCentralFailure } from '../../auth/centralFailureContract';
import { denialReasonFor, raiseBootDenial } from '../../auth/bootDenial';
import { CENTRAL_URL, APP_ID } from '../../config/centralAuth';
import { WorkspaceClosedOverlay } from '../overlays/WorkspaceClosedOverlay';
import { CentralUnavailableOverlay } from '../overlays/CentralUnavailableOverlay';
import { BootReadyEffects, WorkspaceHintEffect } from './BootReadyEffects';

interface BootGateProps {
  children: React.ReactNode;
}

/**
 * Sentinel status for "the round trip never completed" (offline, DNS, reset,
 * abort/timeout). Mirrors RTK Query's own non-HTTP transport status so the one
 * shared classifier treats both Central perimeters identically.
 */
export const TRANSPORT_FAILURE = 'FETCH_ERROR';

/**
 * One-shot latch for the boot-time Central-login transition.
 *
 * The 401 branch below used to refuse to clear auth at all, on the grounds that
 * logout -> /login -> OIDC -> boot -> 401 could loop. The cure was worse than
 * the disease: EVERY 401 became the "Service Unavailable / Retry" overlay, so an
 * ordinary 30-minute session expiry rendered an outage screen whose Retry could
 * never succeed.
 *
 * The loop is now prevented precisely instead of categorically:
 *   - only a Central code that MEANS the session is over sends the user to
 *     login (SESSION_EXPIRED, SESSION_REVOKED, AUTH_REQUIRED, …);
 *   - a 401 that carries no code — the actual "a fresh OIDC token was
 *     rejected" symptom the old comment was about — is a SERVICE failure and
 *     still fails closed to the overlay;
 *   - and this latch allows at most ONE login transition per boot generation,
 *     so even a Central that answered SESSION_EXPIRED to a brand-new token
 *     lands on the overlay the second time instead of ping-ponging.
 *
 * A successful boot re-arms it, so a genuine later expiry is still honoured.
 */
let bootLoginDispatched = false;

/** Re-arm the latch. Called on a successful boot; exported for tests. */
export function resetBootFailureLatch(): void {
  bootLoginDispatched = false;
}

/**
 * Turn one failed switch-context response into boot state.
 *
 * Extracted from the effect below and exported so the contract can be executed
 * directly: this package has no jsdom, so the effect itself cannot be driven,
 * but this function IS the whole failure decision and the component does nothing
 * else with a failure.
 *
 * Every branch comes from the one shared classifier
 * (`src/auth/centralFailureContract.ts`), which `store/api.ts` also uses — so
 * the boot perimeter and ordinary API traffic can no longer disagree about what
 * a code means.
 */
export function applyBootFailure(
  status: number | string,
  body: unknown,
  dispatch: AppDispatch,
): void {
  const decision = classifyCentralFailure({ status, body });

  switch (decision.action) {
    // ── The session is over. Central login — NEVER the outage overlay. ──
    case 'CENTRAL_LOGIN': {
      if (bootLoginDispatched) {
        // Already sent this boot generation to login once and we are back with
        // the same verdict. Refusing to loop is more useful than looping, so
        // fail closed and let the user decide.
        console.error('[BootGate] session reported over twice in one boot — failing closed:', decision.reason);
        dispatch(setBootUnavailable('Central reported the session over after a fresh login'));
        return;
      }
      bootLoginDispatched = true;
      console.warn('[BootGate] session over:', decision.reason);
      dispatch(centralLogout());
      dispatch(setBootUnauthenticated());
      return;
    }

    // ── The access token aged out (15 min) but the session may well be alive. ──
    //
    // Mid-session this is now a SILENT renewal: `store/api.ts` and
    // `auth/reportCentralFailure.ts` both route a TOKEN_EXPIRED through
    // `auth/silentAuthorize.ts`, which re-authorizes against the live Central
    // session in a hidden frame and leaves the SPA mounted.
    //
    // This perimeter deliberately keeps the full-page route, for one reason:
    // it runs ONLY while `bootCompleted` is false — before the application has
    // rendered anything the user could lose. A full-page Central login there is
    // not a silent-vs-loud difference, it is one navigation either way, and it
    // keeps this decision function synchronous and total (it is called directly
    // by tests precisely because no React effect can run in this package).
    //
    // Central is demonstrably up — it just answered — so the outage overlay
    // would be a lie, and the login re-establishes the token from the live
    // session over SSO without prompting for anything.
    case 'RETRY_AFTER_REFRESH': {
      if (bootLoginDispatched) {
        console.error('[BootGate] token still expired after a fresh login — failing closed:', decision.reason);
        dispatch(setBootUnavailable('Access token still expired after a fresh login'));
        return;
      }
      bootLoginDispatched = true;
      console.warn('[BootGate] access token expired and this app has no refresh proxy — re-establishing via Central:', decision.reason);
      dispatch(centralLogout());
      dispatch(setBootUnauthenticated());
      return;
    }

    // ── Closed workspace. Never a login: signing in cannot reopen it. ──
    case 'WORKSPACE_CLOSED':
      raiseBootDenial(dispatch, 'workspace_closed', decision.code, body);
      return;

    // ── Membership gone, or this app not assigned. Suite navigation only. ──
    //
    // Never a login (a fresh token authorises the same dead membership and
    // lands straight back here) and never an outage (Central answered). The
    // suite-navigation overlay offers the workspaces the user CAN still reach,
    // and now carries the REASON, so it no longer calls an access denial
    // "Workspace Closed" (BH-06).
    case 'WORKSPACE_PICKER':
    case 'APP_ACCESS_DENIED':
      console.warn('[BootGate] access denied for this workspace/app:', decision.reason);
      raiseBootDenial(dispatch, denialReasonFor(decision.action) ?? 'app_access_denied', decision.code, body);
      return;

    // ── Central is down. Outage overlay, credential untouched, never login. ──
    case 'OUTAGE':
      console.error('[BootGate] Central unavailable:', decision.reason);
      dispatch(setBootUnavailable(decision.reason));
      return;

    // ── Anything auth-shaped we cannot interpret, or any other non-2xx. ──
    //
    // Boot fails closed. This is where a code-less 401 lands — the "switch-
    // context rejected a fresh OIDC token" case — so the original loop
    // protection is preserved exactly where it was earned, and nowhere else.
    case 'SERVICE_FAILURE':
    case 'PASS_THROUGH':
    default:
      // A 403 from switch-context with a code the classifier does not own is
      // still Central REFUSING entry, not Central being down: the outage
      // overlay's Retry can never succeed for it. Fail closed to the access-
      // denied surface (suite contract 1.3), never to "Service Unavailable".
      if (status === 403) {
        console.warn('[BootGate] switch-context refused entry:', decision.reason);
        raiseBootDenial(dispatch, 'app_access_denied', decision.code, body);
        return;
      }
      console.error('[BootGate] unusable switch-context response:', status, decision.reason);
      dispatch(setBootUnavailable(decision.reason));
      return;
  }
}

/**
 * Permission shape that matches Central's currentWorkspace.permissions.
 * We rebuild it from the simulated role so consumers like
 * useCentralConsumer().hasWorkspacePermission keep working.
 */
function permissionsForRole(role: string): {
  manageMembers: boolean;
  manageBilling: boolean;
  transferOwnership: boolean;
  closeWorkspace: boolean;
} {
  if (role === 'owner') {
    return { manageMembers: true, manageBilling: true, transferOwnership: true, closeWorkspace: true };
  }
  if (role === 'admin') {
    return { manageMembers: true, manageBilling: true, transferOwnership: false, closeWorkspace: false };
  }
  return { manageMembers: false, manageBilling: false, transferOwnership: false, closeWorkspace: false };
}

/**
 * Apply Testing-Mode overrides (sessionStorage) to the workspace object
 * returned by Central's switch-context. Returns the workspace unchanged
 * when no overrides are set.
 *
 * Order of precedence:
 *   1. simulated central role  → workspace.role + workspace.permissions
 *   2. simulated app profile   → workspace.scopes (only when role === 'member')
 *
 * Both overrides are sessionStorage-backed and cleared by the Testing
 * Mode panel's Clear button (and on logout).
 */
export function applyTestingModeOverrides<T extends Record<string, unknown>>(workspace: T | null): T | null {
  if (!workspace) return workspace;

  const simulatedRole = getSimulatedCentralRole();
  const simulatedProfile = getSimulatedAppProfile();
  if (!simulatedRole && !simulatedProfile) return workspace;

  const next: Record<string, unknown> = { ...workspace };

  if (simulatedRole) {
    next.role = simulatedRole;
    next.permissions = permissionsForRole(simulatedRole);
  }

  // App-profile override only meaningful when the effective role is 'member'.
  // Owner / admin / viewer have role-driven access; ignore the bundle for them.
  const effectiveRole = (next.role ?? 'member') as string;
  if (simulatedProfile && effectiveRole === 'member') {
    const scopes = new Set<string>();
    for (const profileKey of simulatedProfile) {
      const bundle = SCOPE_BUNDLES[profileKey];
      if (bundle) for (const s of bundle) scopes.add(s);
    }
    next.scopes = Array.from(scopes);
    // Capabilities are derived from scopes by Central; clear the local
    // capabilities map (set to undefined, NOT empty object) so the shared
    // hasCapability() function falls back to exact-scope checks against
    // the simulated scope set. An empty {} is truthy and would make the
    // shared function think capabilities are enabled but find nothing → deny all.
    next.capabilities = undefined;
  }

  return next as T;
}

export function BootGate({ children }: BootGateProps) {
  const dispatch = useDispatch<AppDispatch>();
  const accessToken = useSelector(selectCentralAccessToken);
  const isAuthenticated = useSelector(selectIsCentralAuthenticated);
  const bootCompleted = useSelector(selectBootCompleted);
  const workspaceClosed = useSelector(selectWorkspaceClosed);
  const centralUnavailable = useSelector(selectCentralUnavailable);
  const isSwitchingWorkspace = useSelector(selectIsSwitchingWorkspace);

  useEffect(() => {
    // Only call switch-context when authenticated and not yet booted.
    // Skip if workspace is closed, Central is unavailable, or a manual switch is in progress.
    if (!isAuthenticated || !accessToken || bootCompleted || workspaceClosed || centralUnavailable || isSwitchingWorkspace) return;

    let cancelled = false;
    const controller = new AbortController();
    // 30s generous timeout → transitions to CentralUnavailableOverlay (one-shot, no loop)
    const timeout = setTimeout(() => controller.abort(), 30_000);

    (async () => {
      try {
        const response = await fetch(`${CENTRAL_URL}/api/suite/switch-context`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-App-Id': APP_ID,
          },
          signal: controller.signal,
        });

        clearTimeout(timeout);
        if (cancelled) return;

        if (response.ok) {
          const data = await response.json();
          // Update token if switch-context returns a new one
          if (data.accessToken) {
            dispatch(updateCentralAccessToken({
              accessToken: data.accessToken,
              expiresIn: data.expiresIn,
            }));
          }

          // Testing-Mode: apply simulated Central role + simulated BeHeard
          // app-profile bundle BEFORE storing in Redux. Central does not
          // re-mint the simulating user's switch-context response, so without
          // this shim the sidebar / settings / role badge / useAppRole all
          // keep showing the tester's REAL role/scopes. Pitchless uses the
          // same pattern (admin-frontend/src/central/BootGateProvider.tsx).
          const simulatedWorkspace = applyTestingModeOverrides(data.workspace);

          // A good boot re-arms the one-shot login latch, so a genuine expiry
          // later in this page's life is still honoured.
          resetBootFailureLatch();

          dispatch(
            setBootReady({
              currentWorkspace: simulatedWorkspace,
              otherWorkspaces: data.otherWorkspaces ?? [],
              apps: data.apps ?? [],
              user: data.user ?? null,
              meta: data.meta,
            })
          );
          return;
        }

        // Every non-2xx goes through the ONE shared classifier.
        const body = await response.json().catch(() => undefined);
        applyBootFailure(response.status, body, dispatch);
      } catch (err) {
        clearTimeout(timeout);
        if (cancelled) return;
        console.error('[BootGate] Central unreachable:', err);
        applyBootFailure(TRANSPORT_FAILURE, undefined, dispatch);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [isAuthenticated, accessToken, bootCompleted, workspaceClosed, centralUnavailable, isSwitchingWorkspace, dispatch]);

  // ── Render gates, in PRECEDENCE order ───────────────────────────────────
  //
  //   1. SESSION_EXPIRED / SESSION_REVOKED / AUTH_REQUIRED → Central login
  //   2. WORKSPACE_CLOSED                                  → workspace-closed UI
  //   3. APP_ACCESS_DENIED                                  → Access Denied
  //   4. CENTRAL_UNAVAILABLE                                → Retry Connection
  //   5. a genuine authenticated data failure               → the page's own error
  //
  // The order used to put `centralUnavailable` and `workspaceClosed` ABOVE
  // `!isAuthenticated`, which inverts (1) and (4). It matters because
  // `bootStatus` is a SINGLE enum field in the shared boot slice, so a later
  // `setBootUnavailable` overwrites an earlier `setBootUnauthenticated` — a
  // terminal auth verdict silently demoted to an outage screen whose Retry can
  // never succeed. `isAuthenticated` is the durable half of the verdict (only a
  // new credential reverses `centralLogout`), so the gate reads THAT first.
  //
  // One exception, kept deliberately ahead of it: a workspace switch in
  // progress. `store/api.ts` refuses to end the session while
  // `isSwitchingWorkspace` is set, so an auth-state blip during a switch is a
  // race and not a verdict, and hiding the switching overlay for it was its own
  // bug.
  //
  // ── HONEST LABEL: structural, NOT an observed failure ──────────────────
  //
  // The overwrite mechanism above is real — `applyBootFailure`'s fail-closed
  // branch does dispatch `setBootUnavailable` after a `setBootUnauthenticated`
  // in the same boot generation, and a late OUTAGE from an in-flight request
  // can land after a terminal verdict too. But this component's inverted order
  // was UNREACHABLE via the router, and the reorder is not credited with
  // fixing anything observed:
  //
  //   every route that mounts <BootGate> mounts it INSIDE a guard that already
  //   answers `!isAuthenticated` — <AuthGuard> for /onboarding and the whole
  //   protected tree, <AdminGuard> for /admin (App.tsx). Both return
  //   <Navigate to="/login"> INSTEAD OF their children, so once the verdict
  //   lands BootGate does not render at all and cannot show the wrong screen.
  //
  // What was genuinely reachable, and what did strand users, was AdminGuard's
  // own ordering: /admin is the one branch with no AuthGuard above it, so
  // AdminGuard WAS the outermost gate and its `isLoading || !bootCompleted`
  // check ran first. That is fixed in AdminGuard.tsx and is the only one of the
  // three reorderings claimed as a fix. This one is defence in depth: it makes
  // the gate correct on its own terms, so it cannot become the live bug the day
  // a <BootGate> is mounted without a guard above it.

  // Switching workspace — full-screen blocking overlay (manual switch in progress).
  // MUST be checked BEFORE !isAuthenticated so a transient auth-state race
  // (from the reauth middleware) doesn't hide the switching overlay.
  if (isSwitchingWorkspace) {
    return (
      <div className="fixed inset-0 z-[9997] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto" />
          <p className="mt-4 text-white text-lg font-medium">Switching workspace…</p>
        </div>
      </div>
    );
  }

  // 1. The session is over → render nothing. AuthGuard / AdminGuard sit above
  //    this component and own the one <Navigate to="/login">; what matters here
  //    is that NO other surface — not the outage overlay, not a spinner, not a
  //    page's own error — gets rendered over a terminal auth state.
  if (!isAuthenticated) {
    return null;
  }

  // 2 + 4. Overlays (CentralUnavailable sits at z-[9999] above WorkspaceClosed).
  if (centralUnavailable) {
    return <CentralUnavailableOverlay />;
  }

  if (workspaceClosed) {
    return (
      <>
        {/* A ?workspaceId hint is honoured from the refused state too (QA-B P2). */}
        <WorkspaceHintEffect />
        <WorkspaceClosedOverlay />
      </>
    );
  }

  // Show loading while boot is pending
  if (!bootCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-500">Verifying workspace…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ?billing= return, focus-after-billing, ?workspaceId= hint (BH-07, contract 1.1/1.2) */}
      <BootReadyEffects />
      {children}
    </>
  );
}
